import { fetchImageAsBase64 } from "./Helpers";

export const EXPORT_APPLICANT_IMAGE_COLUMN = "Image URL";

const EXPORT_ROW_HEIGHT_WITH_IMAGE = 56;
const EXPORT_IMAGE_DISPLAY_SIZE = { width: 48, height: 48 };

const parseBase64Payload = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  const match = dataUrl.match(/^data:image\/[\w+]+;base64,(.+)$/);
  return match ? match[1] : dataUrl;
};

const getExtensionFromDataUrl = (url, dataUrl) => {
  const mime = dataUrl?.match(/^data:image\/([\w+]+);base64,/)?.[1];
  if (mime === "png") return "png";
  if (mime === "jpeg" || mime === "jpg") return "jpeg";
  if (mime === "gif") return "gif";
  if (mime === "webp") return "png";

  const lower = String(url || "").toLowerCase();
  if (lower.includes(".png")) return "png";
  if (lower.includes(".webp")) return "png";
  if (lower.includes(".gif")) return "gif";
  return "jpeg";
};

const fetchProfileImagePayload = async (url) => {
  if (!url || typeof url !== "string" || !url.trim()) {
    return null;
  }

  try {
    const dataUrl = await fetchImageAsBase64(url.trim());
    const base64 = parseBase64Payload(dataUrl);
    if (!base64) return null;
    return {
      base64,
      extension: getExtensionFromDataUrl(url, dataUrl),
    };
  } catch {
    return null;
  }
};

/**
 * Embeds applicant profile images in the Image URL column.
 * @param {object} params
 * @param {import('exceljs').Workbook} params.workbook
 * @param {import('exceljs').Worksheet} params.worksheet
 * @param {object[]} params.rows - row objects keyed by export column names
 * @param {string[]} params.headers - column keys in order (e.g. uniqueHeader)
 * @param {number} params.dataStartRowNumber - 1-based Excel row index of first data row
 * @param {number} [params.columnOffset] - leading columns before `headers` (e.g. "#" in admin export)
 */
export const embedApplicantProfileImages = async ({
  workbook,
  worksheet,
  rows,
  headers,
  dataStartRowNumber,
  columnOffset = 0,
}) => {
  const imageColIndex = headers.indexOf(EXPORT_APPLICANT_IMAGE_COLUMN);
  if (imageColIndex < 0 || !rows?.length) return;

  const excelColNumber = imageColIndex + 1 + columnOffset;

  const payloads = await Promise.all(
    rows.map((row) =>
      fetchProfileImagePayload(row[EXPORT_APPLICANT_IMAGE_COLUMN]),
    ),
  );

  payloads.forEach((payload, rowIndex) => {
    const excelRowNumber = dataStartRowNumber + rowIndex;
    worksheet.getRow(excelRowNumber).height = EXPORT_ROW_HEIGHT_WITH_IMAGE;

    if (!payload) return;

    const imageId = workbook.addImage({
      base64: payload.base64,
      extension: payload.extension,
    });

    worksheet.getCell(excelRowNumber, excelColNumber).value = null;

    worksheet.addImage(imageId, {
      tl: {
        col: imageColIndex + columnOffset + 0.12,
        row: excelRowNumber - 1 + 0.08,
      },
      ext: EXPORT_IMAGE_DISPLAY_SIZE,
    });
  });
};
